"use client";

import { useEffect } from "react";
import useSWR, { type KeyedMutator, type SWRConfiguration } from "swr";
import { useUser } from "../providers/user-provider";

export interface CachedResponse<T> {
  data: T | undefined;
  isLoading: boolean;
  error?: Error;
  mutate: KeyedMutator<{
    data: T | null;
    errorCode?: string | undefined;
    error?: Error | undefined;
  }>;
  isValidating: boolean;
}

interface UseCachedRequestParams<T> {
  url: string | null;
  parser?: (data: object) => T;
  swrOptions?: SWRConfiguration;
  anonymousRequest?: boolean;
}

export function useCachedRequest<T>({
  url,
  parser,
  swrOptions,
  anonymousRequest,
}: UseCachedRequestParams<T>): CachedResponse<T | null> {
  const { buildRequestHeaders, isReady, logout } = useUser();
  const fetcher = async (): Promise<{
    data: T | null;
    errorCode?: string;
    error?: Error;
  }> => {
    if (url === null) {
      throw new Error("URL is null");
    }

    const headers = anonymousRequest
      ? {
          "content-type": "application/json",
        }
      : await buildRequestHeaders();
    const response = await fetch(url, {
      method: "GET",
      headers,
    });
    const data = await response.json();

    if (!response.ok) {
      if (response.status === 401) {
        if (data.error.code === "invalid_or_expired_jwt") {
          return { data: null, errorCode: data.error.code };
        }
        if (!anonymousRequest) {
          logout();
        }
        return { data: null, errorCode: data.error.code };
      }

      if (data.error?.message) {
        return { data: null, error: data.error };
      }

      throw new Error(data.error);
    }

    if (parser) {
      try {
        return { data: parser(data) };
      } catch (error) {
        return { data: null, error: error as Error };
      }
    }

    return { data };
  };

  const isReadyToFetch = anonymousRequest || isReady;

  const { data, error, isLoading, mutate, isValidating } = useSWR(
    isReadyToFetch && url ? url : null,
    fetcher,
    { ...swrOptions },
  );
  useEffect(() => {
    if (data?.errorCode === "invalid_or_expired_jwt") {
      // do nothing, jwt will be refreshed
    }
    if (data?.errorCode === "organization_forbidden") {
      window.location.href = "/organization-select";
    }
  }, [data, error]);

  return {
    data: data?.data || null,
    // if our request is waiting on the user to be ready, we should show loading
    // this is because we don't want to show an empty state if the user is not ready
    isLoading: isLoading || !isReadyToFetch,
    error: error || data?.error,
    mutate,
    isValidating,
  };
}
